/**
 * Trades actually taken, drawn on the chart.
 *
 * A small arrowhead with its tip on the fill price. That is the execution mark
 * NinjaTrader and Sierra Chart use, and it is the reason the shape points
 * sideways rather than up or down: the arrow's job is to say "here, at this
 * price", not to re-state a direction the colour already gives. Teal bought,
 * red sold. Bot entries carry a thin light rim; yours read as the bare fill.
 * Everything else — time, size, fill price, which bot — lives behind a click,
 * in the popup `markerAt()` feeds.
 *
 * ENTRY and EXIT are told apart by which way the arrow faces, not by colour or
 * size — colour is spoken for by direction, and size is spoken for by not
 * shouting. An entry sits LEFT of its bar pointing right, into time. An exit is
 * the mirror image: RIGHT of its bar, pointing left, back at the entry. Same
 * head, same 6x8, same hue rule. Between a matched pair runs a faint dotted
 * connector, tip to tip, so the two arrows point at each other along it — see
 * {@link LINK_COLOUR}. Exits get no bot rim at all: a position the bot opened
 * can be closed by hand, so the marker must not claim otherwise.
 *
 * The vertical position IS the information. The tip lands on the fill price
 * exactly and nothing offsets it away, so the marker can be read against the
 * price scale rather than merely noticed. Clearing the candle is handled on the
 * horizontal axis instead — see {@link tipOffset} — which costs nothing,
 * because time is not what the marker is trying to tell you.
 *
 * Small on purpose. The head is 6x8 CSS px, under half the ink of the 5px
 * up/down triangle it replaces, and it recedes by being small rather than by
 * being faint — so it is drawn opaque where the old one was translucent. See
 * {@link MARKER_ALPHA}: opacity is the one thing that must not do the receding,
 * because colour is the only direction cue a sideways arrow has.
 *
 * No text labels, for the same reason the detail moved behind a click.
 */

import type { ISeriesPrimitive, IPrimitivePaneView, IPrimitivePaneRenderer, Time } from 'lightweight-charts';
import type { TradeMarker } from '../../utils/tradeMarkers';
import { LAYOUT_PRIORITY, MarkerLayoutClient, type LayoutItem, type MarkerLayoutRegistry } from './markerLayout';
import { snapToBar } from '../../utils/tradeMarkers';

/**
 * Bought and sold get the platform's usual green/red. Keyed on the FILL side,
 * on exits as well as entries, so a long round trip reads teal-in / red-out.
 */
const LONG = '#26a69a';
const SHORT = '#ef5350';

/**
 * Half-height of the arrowhead in CSS px — the head is {@link HEAD_HALF} * 2
 * tall, centred on the fill price.
 *
 * 4, at the middle of the range the redesign allowed. Integer rather than 3.5
 * so the back edge and both corners land on device-pixel boundaries at 1x; a
 * shape this small shows a half-pixel edge as a visible smear.
 */
export const HEAD_HALF = 4;

/**
 * Length from the flat back edge to the tip, in CSS px.
 *
 * 6 against a half-height of 4 gives a ~67 degree apex: sharp enough to read as
 * an arrowhead rather than a wedge, blunt enough that it does not dissolve into
 * a sliver at 8px tall. Also integer, for the same pixel-grid reason as
 * {@link HEAD_HALF}. Total ink is 24px^2 against the old triangle's 50.
 */
export const HEAD_LEN = 6;

/**
 * Rim widths, in CSS px. Both are drawn strictly OUTSIDE the head — see the
 * clip in {@link TradeMarkerRenderer.draw} — so they grow the silhouette
 * instead of painting over the direction colour.
 *
 * The two differ because the colours do. The bot rim is near-white and every
 * pixel of it is ink competing with the hue for the eye, so it stays at 1: at
 * 0.75 it reads as antialiasing on a shape this small, and at 2 the marker
 * becomes a white arrow with a coloured centre. The manual keyline is the chart
 * background, so it is invisible except where it is separating the head from a
 * candle — width there is free, and 1.5 is what it took to lift a red entry off
 * a red candle body at 1x.
 */
const RIM_BOT_WIDTH = 1;
const KEYLINE_WIDTH = 1.5;

/**
 * Bot entries get a light rim. What the eye reads is outline-or-not, because
 * the manual rim is drawn in the chart's own background colour and is therefore
 * invisible except where it is doing work. That distinction has to be
 * answerable from ONE marker with nothing beside it to compare against, since
 * every fill today is manual and the side-by-side case never actually happens.
 *
 * Near-white rather than pure white: pure white reads as a selected/hover
 * state. Neutral rather than a tint of the marker's own hue, because a same-hue
 * rim on a 6x8 shape is indistinguishable from antialiasing.
 */
const RIM_BOT = 'rgba(255, 255, 255, 0.9)';

/**
 * Used when the chart will not say what colour its background is. Matches the
 * default theme's `chartBackground`.
 */
const KEYLINE_FALLBACK = '#0a0e27';

/**
 * The manual marker's rim, in the chart's own background colour — a cut, not an
 * outline. It separates; it does not add ink.
 *
 * This is not decoration. At the chart's default bar spacing of 8 a candle body
 * is ~6px wide and the bars are ~2px apart, so a marker placed clear of its OWN
 * candle lands squarely on its neighbour — and the neighbour is painted in the
 * same two colours the markers use. A red entry beside a red candle is
 * camouflage without this. Drawn in the background colour rather than in black
 * so that it vanishes completely when there is nothing behind the marker, which
 * is what keeps "manual" reading as the bare fill.
 *
 * Reads `layout.background` off the live chart, so it follows the theme through
 * all fifty of them rather than assuming one. Gradient backgrounds report
 * `topColor` instead of `color`; either will do, since this only has to be
 * close, not exact.
 *
 * Pure and exported for the tests.
 */
export function keylineColour(chartOptions: unknown): string {
  const bg = (chartOptions as { layout?: { background?: Record<string, unknown> } })?.layout
    ?.background;
  const c = bg?.color ?? bg?.topColor;
  return typeof c === 'string' && c.trim().length > 0 ? c : KEYLINE_FALLBACK;
}

/**
 * Opaque — a deliberate reversal of the 0.85/0.65 the old triangle used, and
 * the one number here that was settled by looking rather than by reasoning.
 *
 * That triangle was 5px half-extent with an 8px ring, so it needed opacity to
 * stop shouting. This head is under half the ink and already recedes on its
 * own. Worse, it sits ON a candle at any normal zoom (see
 * {@link keylineColour}), and every 10% of alpha spent lets that candle bleed
 * up through the one channel carrying direction. Rendered over a real run of
 * bars at 0.9 the manual markers were already hard to find; at 0.65 they were
 * gone. The recession is entirely in the size, which is where it belongs — the
 * ask was for a smaller marker, not a fainter one.
 */
const MARKER_ALPHA = 1;

/**
 * The entry-to-exit connector: neutral, and barely there.
 *
 * NEUTRAL, not the trade's own hue. Teal and red are already fully committed on
 * this chart — candles, both arrowheads, the position line — and a coloured
 * connector would add a third thing shouting the same word. Slate reads as
 * annotation on sight precisely because nothing else here is that colour.
 *
 * 0.32 alpha over the default #0a0e27 backdrop lands the line around the
 * weight of the grid, which is the intended rank: findable when you look along
 * a trade, invisible when you are reading price. Much under 0.25 and it breaks
 * up against a candle body; much over 0.4 and it starts competing with the
 * session dividers and the S/R levels, which are real chart lines and must stay
 * louder than a piece of annotation.
 *
 * Exported so a test can hold the faintness to account — this is the one part
 * of the marker that is supposed to be hard to see, and "make it a bit clearer"
 * is a one-character change away at all times.
 */
export const LINK_COLOUR = 'rgba(148, 163, 184, 0.62)';

/**
 * Dotted, not dashed: [1, 3] CSS px is a 1px dot with a 3px hole, so the line
 * is a quarter ink. A dash pattern at this weight starts reading as a real
 * chart line — this chart already has plenty of those — while dots read as
 * something drawn ON the chart rather than by it. Scaled by the device pixel
 * ratio at draw time, or the dots vanish on a retina display.
 */
export const LINK_DASH: readonly number[] = [2, 3];

/** Hairline. Any thicker and the dots merge into a dashed line. */
export const LINK_WIDTH = 1.2;

/**
 * Floor for the gap between the bar's centre and the arrow tip, in CSS px.
 *
 * Only a floor — the real gap is computed from bar spacing so the head clears
 * the candle body at any zoom. See {@link tipOffset}.
 */
export const TIP_GAP_MIN = 5;

/** Air left between the candle's near edge and the tip, in CSS px. */
const TIP_CLEARANCE = 1;

/**
 * Half a candlestick body, as a fraction of bar spacing.
 *
 * lightweight-charts sizes bodies with `optimalCandlestickWidth`, which lands
 * between 0.8 and 1.0 of the spacing depending on zoom. 0.45 is the
 * conservative half of the wide end.
 */
const HALF_BODY_OF_SPACING = 0.45;

/**
 * Farthest any painted pixel gets from the anchor the hit test uses.
 *
 * The anchor is the tip; the head runs back and outward from there, and the
 * wider of the two rims adds exactly its own width on both axes — exactly,
 * because the rim is joined round rather than mitred, so the point does not
 * spike out past it. Asserted against {@link HIT_RADIUS} in the tests, so the
 * whole visible marker stays clickable.
 */
const WIDEST_RIM = Math.max(RIM_BOT_WIDTH, KEYLINE_WIDTH);
export const ARROW_REACH = Math.hypot(HEAD_LEN + WIDEST_RIM, HEAD_HALF + WIDEST_RIM);

/**
 * Click slop, in CSS px — INTENTIONALLY larger than the marker.
 *
 * The head is 6x8 and its farthest corner is {@link ARROW_REACH} (~9.3px) from
 * the anchor. Nobody can click a 6x8 target, and on touch it is hopeless, so
 * this stays at 11: it covers the whole head plus its rim with room over, and
 * reaches past the tip far enough to catch a click aimed at the candle the
 * marker belongs to. These two numbers answer different questions and are meant
 * to disagree — do not "fix" it by matching them, which either bloats the
 * marker or breaks the click. The redesign shrank the marker, so the gap is
 * wider now than it was, on purpose.
 */
export const HIT_RADIUS = 11;

interface Point {
  x: number;
  y: number;
}

/**
 * Named once and referred to by alias everywhere else. The eslint config does
 * not declare browser globals, so every mention of the real name is a spurious
 * `no-undef`; one alias keeps that to a single suppressed-by-convention hit
 * instead of one per signature.
 */
type Ctx = CanvasRenderingContext2D;

/**
 * Which way an arrowhead faces along x. `1` points right (an entry, sitting
 * left of its bar); `-1` points left (an exit, sitting right of it).
 */
export type ArrowDir = 1 | -1;

/**
 * The three corners of the arrowhead: tip first, then the back edge top and
 * bottom. Pure, so the geometry can be checked without a canvas.
 *
 * `xScale`/`yScale` carry the canvas pixel ratios, which is why the renderer
 * calls this rather than duplicating the maths — one definition of the shape,
 * used by both the drawing and the tests. Three small objects per marker per
 * frame is not worth avoiding; the marker count is in the tens.
 *
 * `dir` mirrors the head about its own tip, which is what makes the exit marker
 * the same shape as the entry rather than a second shape that has to be kept in
 * step with it. It defaults to right, so every entry call site reads exactly as
 * it did before exits existed.
 */
export function arrowHeadPoints(
  tipX: number,
  tipY: number,
  xScale = 1,
  yScale = 1,
  dir: ArrowDir = 1
): [Point, Point, Point] {
  // Signed: the body always lies BEHIND the tip, and behind is whichever way
  // the arrow is not pointing.
  const len = HEAD_LEN * xScale * dir;
  const half = HEAD_HALF * yScale;
  return [
    { x: tipX, y: tipY }, // the tip, on the fill price
    { x: tipX - len, y: tipY - half }, // back edge, above the price
    { x: tipX - len, y: tipY + half }, // back edge, below it
  ];
}

/**
 * How far from the bar's centre the arrow tip sits, in CSS px — to the LEFT for
 * an entry, the same distance to the RIGHT for an exit.
 *
 * Scales with bar spacing so the head clears the candle body rather than
 * covering the bar it is marking — a fixed offset would be swallowed the moment
 * the user zooms in. {@link TIP_GAP_MIN} is the floor, so at the default
 * spacing (8) the answer is just 5 and the marker does not drift about while
 * the chart is being panned.
 *
 * Falls back to the floor when the time scale will not say — a missing or
 * nonsensical spacing is not a reason to drop the marker.
 */
export function tipOffset(barSpacing: number | null | undefined): number {
  if (typeof barSpacing !== 'number' || !Number.isFinite(barSpacing) || barSpacing <= 0) {
    return TIP_GAP_MIN;
  }
  return Math.max(TIP_GAP_MIN, barSpacing * HALF_BODY_OF_SPACING + TIP_CLEARANCE);
}

export interface RenderItem {
  /** {@link TradeMarker.id}, carried through so a hit resolves back. */
  id: string;
  /** The arrow TIP, not the bar centre — the marker is drawn back from here. */
  x: number;
  y: number;
  side: 'buy' | 'sell';
  /** Never true on an exit — see the module note. */
  isBot: boolean;
  /** Right for an entry, left for an exit. See {@link ArrowDir}. */
  dir: ArrowDir;
  /** {@link TradeMarker.closes}, carried so `linkItems` can resolve the pairs. */
  closes: readonly string[];
  /**
   * Where the signal behind this bot entry sits, in CSS px, if it could be
   * placed. Absent on hand-taken fills, on exits, and when the signal bar is
   * off the loaded range.
   */
  signal?: { x: number; y: number };
  /** Bar the fill snapped to, seconds (the shared layout groups by it). */
  barTime?: number;
}

/** One connector, in CSS px: entry tip to exit tip. */
export interface LinkItem {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Nearest placed marker within `radius` of the point, or null.
 *
 * Nearest rather than first-found: markers stack up when several fills land in
 * one bar at similar prices, and with slop this wide their hit circles overlap
 * routinely. Topmost-drawn would be an arbitrary answer (paint order is just
 * fill order); nearest is the one the eye was aiming at.
 *
 * Pure and exported so the hit-test can be tested without a chart.
 */
/**
 * One connector per exit, drawn to the most recent entry that exit closed.
 *
 * `closes` names EVERY lot the fill consumed, and one fill can consume many: an
 * 8-contract sell against eight separate 1-lot entries closes all eight. Drawing
 * a line each turned that exit into a fan reaching back across the whole
 * session — on 2026-08-06 a single exit had seven dotted lines converging on it
 * and the chart was unreadable through them.
 *
 * The most recent lot is chosen rather than the oldest because it is the nearest
 * on screen and the one the eye pairs the exit with. That is a DRAWING choice
 * only: every lot is still closed, still counted, and still in the realised
 * P&L, and `closes` is left whole so the popup can list them all.
 *
 * A pair whose entry is not on screen draws nothing, same as before — half a
 * connector running off the edge reads as a trade opened at the edge.
 *
 * Pure and exported so it can be tested without a chart, like the other
 * geometry helpers here.
 */
/**
 * Screen position of a signal, snapped to its bar. `undefined` when there is no
 * signal, or when it falls outside the loaded bars.
 *
 * Split out of `renderItems` so that loop stays readable — it is the same
 * snap-then-convert the marker itself goes through, just for a second point.
 */
function placeSignal(
  signal: { timestamp: number; price: number } | undefined,
  barTimes: readonly number[],
  timeScale: { timeToCoordinate: (t: Time) => number | null },
  series: { priceToCoordinate: (p: number) => number | null } | null
): { x: number; y: number } | undefined {
  if (!signal || !series) return undefined;
  const barTime = snapToBar(signal.timestamp / 1000, barTimes);
  if (barTime === null) return undefined;
  const x = timeScale.timeToCoordinate(barTime as Time);
  const y = series.priceToCoordinate(signal.price);
  if (x === null || y === null) return undefined;
  return { x, y };
}

export function connectorEndpoints(items: readonly RenderItem[]): LinkItem[] {
  // Keyed lookup rather than a scan per link: several exits can name the same
  // entry, and both lists are the same length as the marker set.
  const byId = new Map<string, RenderItem>();
  for (const it of items) byId.set(it.id, it);

  const out: LinkItem[] = [];
  for (const it of items) {
    // Signal -> entry, for bot entries that could resolve one. Same dotted
    // style as the entry-to-exit line below, deliberately: both answer "these
    // two points are one trade", and giving them different treatments would
    // imply a distinction that is not there. Read together a bot trade becomes
    // signal -> fill -> close in one continuous thread.
    if (it.signal) {
      out.push({ x1: it.signal.x, y1: it.signal.y, x2: it.x, y2: it.y });
    }

    let nearest: RenderItem | null = null;
    for (const entryId of it.closes) {
      const from = byId.get(entryId);
      if (!from) continue;
      if (!nearest || from.x > nearest.x) nearest = from;
    }
    if (nearest) out.push({ x1: nearest.x, y1: nearest.y, x2: it.x, y2: it.y });
  }
  return out;
}

export function hitTestMarkers<T extends { x: number; y: number }>(
  items: readonly T[],
  px: number,
  py: number,
  radius: number = HIT_RADIUS
): T | null {
  // Compare squared distances — same ordering, no square roots.
  const maxD2 = radius * radius;
  let best: T | null = null;
  let bestD2 = Infinity;
  for (const it of items) {
    const dx = px - it.x;
    const dy = py - it.y;
    const d2 = dx * dx + dy * dy;
    if (d2 > maxD2) continue; // outside the slop entirely
    // Strictly nearer: two exactly-equidistant markers resolve to the first,
    // so repeated clicks on the same pixel keep returning the same one.
    if (d2 < bestD2) {
      best = it;
      bestD2 = d2;
    }
  }
  return best;
}

/**
 * Trace the head into the current path. Deliberately does NOT call
 * `beginPath()`, so the caller can add it as a subpath — the rim needs it
 * inside a two-subpath even-odd clip.
 */
function addHead(
  ctx: Ctx,
  tipX: number,
  tipY: number,
  xScale: number,
  yScale: number,
  dir: ArrowDir
): void {
  const [tip, backTop, backBottom] = arrowHeadPoints(tipX, tipY, xScale, yScale, dir);
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(backTop.x, backTop.y);
  ctx.lineTo(backBottom.x, backBottom.y);
  ctx.closePath();
}

class TradeMarkerRenderer implements IPrimitivePaneRenderer {
  private _items: RenderItem[] = [];
  private _links: LinkItem[] = [];
  private _keyline: string = KEYLINE_FALLBACK;

  update(items: RenderItem[], links: LinkItem[], keyline: string): void {
    this._items = items;
    this._links = links;
    this._keyline = keyline;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as Ctx;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      // A rim is a stroke, which has no axis; the smaller ratio keeps it from
      // going fatter in CSS px on whichever axis is scaled less.
      const ratio = Math.min(hr, vr);
      const botRimPx = RIM_BOT_WIDTH * ratio;
      const keylinePx = KEYLINE_WIDTH * ratio;
      // Big enough to contain the head and its rim; the even-odd clip below
      // subtracts the head from it.
      const box = (HEAD_LEN + HEAD_HALF + WIDEST_RIM + 2) * Math.max(hr, vr);

      // Connectors FIRST, so every head paints over the end of its own line and
      // the dots never nibble at the arrow tips. One path for all of them, one
      // stroke: the dash phase restarts at each moveTo, so batching them cannot
      // make one line's dots depend on another's length.
      //
      // Not snapped to whole device pixels the way the heads are. A connector is
      // a diagonal of arbitrary slope, so there is no edge for snapping to
      // sharpen — and rounding both ends would tilt the line by up to a pixel
      // over its length, which on a near-horizontal one is visible.
      if (this._links.length > 0) {
        ctx.save();
        // Explicit, not inherited. The faintness lives entirely in LINK_COLOUR's
        // own alpha, so a leftover globalAlpha from another primitive sharing
        // this canvas must not get to multiply it.
        ctx.globalAlpha = 1;
        ctx.strokeStyle = LINK_COLOUR;
        ctx.lineWidth = Math.max(1, LINK_WIDTH * ratio);
        // Butt, not round: round caps on a 1px dot inflate it by half its own
        // width at each end and close the gaps back up into a solid line.
        ctx.lineCap = 'butt';
        ctx.setLineDash(LINK_DASH.map((d) => d * ratio));
        ctx.beginPath();
        for (const l of this._links) {
          ctx.moveTo(l.x1 * hr, l.y1 * vr);
          ctx.lineTo(l.x2 * hr, l.y2 * vr);
        }
        ctx.stroke();
        ctx.restore();
      }

      for (const it of this._items) {
        // Snapped to whole device pixels. The back edge is a vertical line and
        // the head is only 6x8 CSS px — landing it half a pixel off smears the
        // one hard edge the shape has. Half a device pixel of price error is
        // below what the price line itself resolves.
        const tx = Math.round(it.x * hr);
        const ty = Math.round(it.y * vr);

        ctx.save();
        ctx.globalAlpha = MARKER_ALPHA;

        // Direction, as a solid mass of colour. Filled for bot and manual
        // alike: which way the arrow faces is spoken for by entry vs exit, so
        // hue is the only thing saying bought from sold and nothing is allowed
        // to dilute it.
        ctx.beginPath();
        addHead(ctx, tx, ty, hr, vr, it.dir);
        ctx.fillStyle = it.side === 'buy' ? LONG : SHORT;
        ctx.fill();

        // The rim. Light on bot entries — that is the whole of the bot/manual
        // channel — and the chart's own background colour on manual ones, where
        // it cuts the head away from whatever candle it has landed on and is
        // invisible everywhere else.
        //
        // EXITS always take the keyline branch, never the light one. That is
        // not the same as "no rim": the keyline is a cut rather than an
        // outline, and without it a red exit beside a red candle is camouflage.
        // What an exit must not have is the BOT rim, which would claim to know
        // who closed the position.
        //
        // Clipped to "a box MINUS the head", so a stroke of twice the rim width
        // lands entirely outside the shape: the silhouette grows by exactly the
        // rim width and not one pixel of rim falls on the fill. Stroking the
        // path directly would eat ~20% of a head this small and wash the hue
        // out, and stroking wide UNDER a fill at alpha < 1 lets the rim bleed
        // back through. Both were tried and both muddied the colour.
        ctx.save();
        ctx.beginPath();
        // Square and centred on the tip, so it contains the head whichever way
        // it is mirrored.
        ctx.rect(tx - box, ty - box, box * 2, box * 2);
        addHead(ctx, tx, ty, hr, vr, it.dir);
        ctx.clip('evenodd');
        ctx.beginPath();
        addHead(ctx, tx, ty, hr, vr, it.dir);
        // Round, not mitred: a mitred join on a ~67 degree apex throws a spike
        // ~1.8x the rim width past the tip, which blunts the point and makes a
        // bot arrow visibly longer than a manual one at the same price.
        ctx.lineJoin = 'round';
        ctx.lineWidth = (it.isBot ? botRimPx : keylinePx) * 2; // inner half is clipped away
        ctx.strokeStyle = it.isBot ? RIM_BOT : this._keyline;
        ctx.stroke();
        ctx.restore();

        ctx.restore();
      }
    });
  }
}

class TradeMarkerPaneView implements IPrimitivePaneView {
  constructor(private readonly _source: TradeMarkerPrimitive) {}
  private readonly _renderer = new TradeMarkerRenderer();

  update(): void {
    // Placed once and handed to both: the connectors are drawn between the very
    // coordinates the heads are drawn at, so a line cannot end anywhere but on
    // an arrow tip. Calling renderItems() twice would also mean placing every
    // marker twice per frame.
    const items = this._source.renderItems();
    this._renderer.update(items, this._source.linkItems(items), this._source.keyline());
  }

  renderer(): IPrimitivePaneRenderer {
    return this._renderer;
  }
}

export class TradeMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: TradeMarker[] = [];
  /**
   * Whether the entry-to-exit connectors are drawn. Pushed in by ChartView from
   * `tradeMarkerStore` rather than read here: a primitive that subscribed to a
   * store would be untestable without one, and every other input to this class
   * already arrives the same way.
   *
   * Defaults true, matching the store's own default, so a primitive constructed
   * before the first push does not blink the lines off for a frame.
   */
  private _showLinks = true;
  private _series: any = null;
  private _chart: any = null;
  private _requestUpdate?: () => void;
  private readonly _views = [new TradeMarkerPaneView(this)];
  /**
   * Shared marker layout: fills are FIXED boxes (the tip is the price, nothing
   * may move it); the other markers flow around them. Two fills whose heads
   * would overlap leave the later one in a "+N" chip.
   */
  private _layout = new MarkerLayoutClient('trades', () => this._layoutItems());

  /** Ascending bar times in seconds — see `_barTimes()`. */
  private _barTimeCache: number[] = [];
  private _barTimesStale = true;
  private readonly _onDataChanged = (): void => {
    this._barTimesStale = true;
  };

  attached(param: any): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._barTimesStale = true;
    // The chart tells us when bars are added or replaced; that is the only
    // thing that can invalidate the snap index.
    this._series?.subscribeDataChanged?.(this._onDataChanged);
    this._layout.setAttached(true);
  }

  /** Join the chart's shared marker layout (null: draw every fill as placed). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(): LayoutItem[] {
    const w = HEAD_LEN + WIDEST_RIM * 2;
    const h = (HEAD_HALF + WIDEST_RIM) * 2;
    return this._placeAll().map((it) => ({
      key: it.id,
      time: it.barTime ?? 0,
      x: it.x,
      side: it.side === 'buy' ? 'below' : 'above',
      anchorY: it.y,
      w,
      h,
      // The head runs back from the tip: left of it on an entry, right on an exit.
      dx: it.dir === 1 ? -(HEAD_LEN + WIDEST_RIM) : -WIDEST_RIM,
      priority: LAYOUT_PRIORITY.fixed,
      fixed: true,
      label: `${it.side === 'buy' ? 'bought' : 'sold'}${it.dir === -1 ? ' (exit)' : ''}`,
    }));
  }

  detached(): void {
    this._layout.setAttached(false);
    try {
      this._series?.unsubscribeDataChanged?.(this._onDataChanged);
    } catch {
      // series already torn down — nothing to unsubscribe from
    }
    this._series = null;
    this._chart = null;
    this._requestUpdate = undefined;
    this._barTimeCache = [];
    this._barTimesStale = true;
  }

  updateAllViews(): void {
    this._views.forEach((v) => v.update());
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this._views;
  }

  setMarkers(markers: TradeMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  /**
   * Show or hide the entry-to-exit connectors. The markers themselves are
   * unaffected — this is a line-only preference, and hiding the lines must not
   * quietly hide the exits they join.
   */
  setShowLinks(show: boolean): void {
    if (this._showLinks === show) return;
    this._showLinks = show;
    this._requestUpdate?.();
  }

  /**
   * The colour a manual marker's keyline is cut in — the chart's background,
   * read live so a theme change is picked up on the next redraw rather than at
   * the next reattach. See {@link keylineColour}.
   */
  keyline(): string {
    return keylineColour(this._chart?.options?.());
  }

  /**
   * Screen-space hit test against the ARROWHEAD, not the bar. Returns the id of
   * the marker under the point, or null.
   *
   * Named markerAt rather than hitTest, matching `CandidatePrimitive`:
   * ISeriesPrimitive already declares a hitTest with a different signature, and
   * shadowing it breaks the interface.
   *
   * Tests against the same placed coordinates the renderer draws from — the tip
   * of each arrow, one source for both — so the click target cannot drift away
   * from the visible marker when the placement changes. The generous slop is
   * deliberate; see {@link HIT_RADIUS}.
   */
  markerAt(px: number, py: number): string | null {
    return hitTestMarkers(this.renderItems(), px, py)?.id ?? null;
  }

  /**
   * Ascending bar times in seconds, cached.
   *
   * `ISeriesApi.data()` (lightweight-charts 5.1.0) rebuilds the whole array —
   * a fresh object per bar — on every call, and `renderItems()` runs on every
   * redraw. Calling it per frame would churn the heap for data that only
   * changes when the chart says it did.
   */
  private _barTimes(): readonly number[] {
    if (!this._barTimesStale) return this._barTimeCache;

    const data = this._series?.data?.() ?? [];
    const times: number[] = [];
    for (const d of data) {
      // Whitespace/gap items carry a `time` but no OHLC. They still occupy a
      // slot on the time scale, so they are a valid snap target — only the
      // time matters here. Times that aren't numbers (`BusinessDay`) can't be
      // compared against an epoch fill time; this chart only ever sets
      // `UTCTimestamp` seconds, so skipping them is safe and keeps the array
      // sortable.
      if (typeof d?.time === 'number') times.push(d.time);
    }

    this._barTimeCache = times;
    this._barTimesStale = false;
    return times;
  }

  /** The fills to draw: all of them, minus any the shared layout folded into a chip. */
  renderItems(): RenderItem[] {
    const all = this._placeAll();
    const shared = this._layout.placed();
    if (!shared || shared.length === all.length) return all;
    const kept = new Set(shared.map((p) => p.item.key));
    return all.filter((it) => kept.has(it.id));
  }

  private _placeAll(): RenderItem[] {
    if (!this._series || !this._chart) return [];
    const timeScale = this._chart.timeScale();
    // Built once per redraw, then binary-searched per marker — never scanned.
    const barTimes = this._barTimes();
    // One lookup per redraw, not per marker: every marker on the pane sits at
    // the same distance from its bar.
    const gap = tipOffset(timeScale.options?.()?.barSpacing);
    const out: RenderItem[] = [];

    for (const m of this._markers) {
      // Fills happen at wall-clock times; bars sit on the bar interval. The
      // chart can only place a marker on a time it holds a data point for —
      // `timeToCoordinate` returns null for anything else — so a raw fill time
      // is silently dropped far more often than not. See `snapToBar`.
      const barTime = snapToBar(m.timestamp / 1000, barTimes);
      // No containing bar: the fill predates the oldest loaded bar, or nothing
      // is plotted yet. It cannot be placed.
      if (barTime === null) continue;
      const x = timeScale.timeToCoordinate(barTime as Time);
      const y = this._series.priceToCoordinate(m.price);
      if (x === null || y === null) continue;
      // Entries go left of the bar and point right; exits are the mirror image.
      // Either way the head is clear of the candle it belongs to, and the side
      // it sits on is what says which kind it is.
      const dir: ArrowDir = m.kind === 'exit' ? -1 : 1;
      out.push({
        id: m.id,
        // Clear of the bar, so the head never covers the candle; the tip is the
        // anchor, and it is what the hit test measures from.
        x: x - gap * dir,
        // Dead on the fill price. No offset — the height is the point.
        y,
        side: m.side,
        // An exit never claims an owner, whatever the fill row says. See the
        // module note: the bot that opened a position is not evidence about who
        // closed it.
        isBot: m.kind === 'entry' && m.botName !== null,
        dir,
        closes: m.closes,
        // Placed the same way the marker was — snapped to a bar, then converted
        // — so the line lands on the signal candle rather than between two of
        // them. Dropped silently if the signal predates the loaded range, which
        // is routine: executions go back further than the chart does.
        signal: placeSignal(m.signal, barTimes, timeScale, this._series),
        barTime,
      });
    }
    return out;
  }

  /**
   * The connectors, in CSS px, for a set of already-placed markers.
   *
   * Takes the placed items rather than re-deriving them, so a line's endpoints
   * are by construction the same two points the arrow tips are drawn at.
   *
   * Tip to tip, not bar to bar. The entry's tip faces right and the exit's
   * faces left, so the line leaves one arrow along the direction it points and
   * arrives at the other head on. Joining the bar centres instead would run the
   * line through both candles and leave both arrows floating beside it.
   *
   * A pair whose entry could not be placed — it predates the oldest loaded bar,
   * which is routine, since fills go back further than the chart does — draws no
   * line at all. Half a connector reaching off the left edge would read as the
   * trade having been opened at the edge of the screen.
   */
  linkItems(items: readonly RenderItem[]): LinkItem[] {
    if (!this._showLinks) return [];
    return connectorEndpoints(items);
  }
}
